import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Typography,
  Box,
} from '@mui/material'
import {
  InsertDriveFileOutlined as InsertDriveFile,
  CloudUploadOutlined as CloudUpload,
  AttachFile,
  Close,
} from '@mui/icons-material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import { useTranslation } from 'react-i18next'

import DocumentView from '../containers/DocumentView'
import DocumentModal from '../containers/DocumentModal'
import { DetailButton } from './detail'
import './detail/detail.css'

interface DocumentSelectorProps {
  label: string
  file: File | string | null
  onFileChange: (file: File | string | null) => void
  displayFileName?: string
  previewUrl?: string
  existingFiles?: { name: string; url: string }[]
  allowSelectExisting?: boolean
  fileType?: string
}

const DocumentSelector: React.FC<DocumentSelectorProps> = ({
  label,
  file,
  onFileChange,
  displayFileName,
  previewUrl: externalPreviewUrl,
  existingFiles = [],
  allowSelectExisting = false,
  fileType = 'application/pdf',
}) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [openExistingModal, setOpenExistingModal] = useState(false)
  const [openPreview, setOpenPreview] = useState(false)
  const [error, setError] = useState('')
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [libraryPreview, setLibraryPreview] = useState<{
    name: string
    url: string
  } | null>(null)
  const [dragOver, setDragOver] = useState(false)

  useEffect(() => {
    if (file instanceof File) {
      const objectUrl = URL.createObjectURL(file)
      setPreviewUrl(objectUrl)
      return () => URL.revokeObjectURL(objectUrl)
    }
    if (externalPreviewUrl) {
      setPreviewUrl(externalPreviewUrl)
      return
    }
    if (typeof file === 'string') {
      setPreviewUrl(file)
      return
    }
    setPreviewUrl(null)
  }, [file, externalPreviewUrl])

  const acceptFile = (selectedFile: File | undefined | null) => {
    if (!selectedFile) return
    if (fileType && selectedFile.type && selectedFile.type !== fileType) {
      setError(
        t('documentSelector.pdfOnly', {
          defaultValue: 'Only PDF files are allowed.',
        })
      )
      return
    }
    setError('')
    onFileChange(selectedFile)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(event.target.files?.[0])
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleSelectExisting = (url: string) => {
    onFileChange(url)
    setOpenExistingModal(false)
  }

  const getFileName = () => {
    if (displayFileName) return displayFileName
    if (!file) return ''
    if (file instanceof File) return file.name
    return file.split('/').pop() || file
  }

  const pickFile = () => inputRef.current?.click()

  const previewDoc =
    file instanceof File
      ? null
      : {
          path: previewUrl || undefined,
          originalName: getFileName(),
          mimeType: fileType,
        }

  return (
    <div className="support-panel">
      {label ? (
        <p
          className="incyia-ffield-label"
          style={{ marginBottom: 8 }}
        >
          {label}
        </p>
      ) : null}

      <input
        ref={inputRef}
        type="file"
        accept={fileType}
        hidden
        onChange={handleFileChange}
      />

      {!file ? (
        <div
          className={`support-dropzone${dragOver ? ' is-drag' : ''}`}
          onDragOver={(e) => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragOver(false)
            acceptFile(e.dataTransfer.files?.[0])
          }}
        >
          <span className="support-drop-ico" aria-hidden>
            <CloudUpload sx={{ fontSize: 32 }} />
          </span>
          <p className="support-drop-title">
            {t('documentSelector.dragDropOrClick', {
              defaultValue: 'Drop a file here, or browse',
            })}
          </p>
          <p className="support-drop-msg">
            {t('taskDetailsPage.support.uploadHint', {
              defaultValue:
                'Upload a PDF to attach proof or supporting material.',
            })}
          </p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <DetailButton onClick={pickFile}>
              <CloudUpload sx={{ fontSize: 16 }} />
              {t('buttons.uploadFile', { defaultValue: 'Upload PDF' })}
            </DetailButton>
            {allowSelectExisting && existingFiles.length > 0 ? (
              <DetailButton
                variant="ghost"
                onClick={() => setOpenExistingModal(true)}
              >
                <AttachFile sx={{ fontSize: 16 }} />
                {t('buttons.selectExisting', {
                  defaultValue: 'Select from Library',
                })}
              </DetailButton>
            ) : null}
          </div>
          {error ? <p className="support-drop-error">{error}</p> : null}
        </div>
      ) : (
        <div className="support-file-card">
          <div className="support-file-main">
            <span className="support-file-ico" aria-hidden>
              <InsertDriveFile sx={{ fontSize: 22 }} />
            </span>
            <div className="support-file-meta">
              <p className="support-file-name">{getFileName()}</p>
              <p className="support-file-hint">
                {t('taskDetailsPage.support.attached', {
                  defaultValue: 'Attached support document (PDF)',
                })}
              </p>
            </div>
          </div>
          <div className="support-file-actions">
            <DetailButton
              variant="ghost"
              size="sm"
              onClick={() => setOpenPreview(true)}
            >
              <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
              {t('buttons.view', { defaultValue: 'View' })}
            </DetailButton>
            <DetailButton size="sm" onClick={pickFile}>
              <SwapHorizOutlinedIcon sx={{ fontSize: 16 }} />
              {t('buttons.replace', { defaultValue: 'Replace' })}
            </DetailButton>
            <DetailButton variant="ghost" size="sm" onClick={() => onFileChange(null)}>
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
              {t('buttons.remove', { defaultValue: 'Remove' })}
            </DetailButton>
          </div>
        </div>
      )}

      {previewDoc ? (
        <DocumentModal
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          document={previewDoc}
        />
      ) : (
        <Dialog
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ className: 'incyia-modal-paper' }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {getFileName()}
            <button
              type="button"
              className="incyia-modal-x"
              onClick={() => setOpenPreview(false)}
              aria-label="Close"
            >
              <Close sx={{ fontSize: 18 }} />
            </button>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
            {previewUrl ? (
              <iframe
                src={previewUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title="PDF Preview"
              />
            ) : null}
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        open={openExistingModal}
        onClose={() => setOpenExistingModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: 'incyia-modal-paper' }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <AttachFile sx={{ mr: 1 }} />
          {t('headers.selectFile', 'Select from Existing Files')}
        </DialogTitle>
        <DialogContent dividers>
          {existingFiles.length === 0 ? (
            <Box py={4} textAlign="center">
              <Typography color="textSecondary">
                {t('messages.noFilesFound', 'No files available.')}
              </Typography>
            </Box>
          ) : (
            <List>
              {existingFiles.map((f, index) => (
                <ListItem
                  key={index}
                  divider={index !== existingFiles.length - 1}
                >
                  <ListItemIcon>
                    <InsertDriveFile />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle2"
                        onClick={() => setLibraryPreview(f)}
                        sx={{
                          cursor: 'pointer',
                          fontWeight: 600,
                          color: 'primary.main',
                        }}
                      >
                        {f.name}
                      </Typography>
                    }
                    secondary={t(
                      'labels.previouslyUploaded',
                      'Previously Uploaded'
                    )}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSelectExisting(f.url)}
                    sx={{ ml: 2, textTransform: 'none' }}
                  >
                    {t('buttons.select', 'Select')}
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExistingModal(false)} color="inherit">
            {t('buttons.cancel', 'Cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(libraryPreview)}
        onClose={() => setLibraryPreview(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ className: 'incyia-modal-paper' }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{libraryPreview?.name}</Typography>
            <button
              type="button"
              className="incyia-modal-x"
              onClick={() => setLibraryPreview(null)}
              aria-label="Close"
            >
              <Close sx={{ fontSize: 18 }} />
            </button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
          {libraryPreview ? (
            <DocumentView
              document={{
                path: libraryPreview.url,
                originalName: libraryPreview.name,
                mimeType: fileType,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default DocumentSelector
